<template>
  <div class="select-suggestion">
      <slot name="label">
        <div
          v-if="label || $t(`suggestable_models.${this.model}`)"
          :class="`label-div text-sm ${focused ? 'text-primary' : ''}`
        ">
          <label>{{ label || $t(`suggestable_models.${this.model}` )}}</label>
        </div>
      </slot>
      <div class="flex flex-row gap-2 w-full">
        <div class="min-h-full w-full relative">
          <vs-select
            ref="select"
            class="w-full"
            autocomplete
            v-model="selectedModelId"
            @input-change="searchSuggestions"
            :placeholder="translatedPlaceholderText"
            :danger="selectedModel === undefined"
            danger-text="Seleção inválida"
            @input="selectModel"
            :noData="noData"
            @focus="onFocus"
            @blur="focused = false"
            :disabled="isDisabled">
            <vs-select-item
              :key="model.id"
              :value="model.id"
              :text="itemTextFormat(model)"
              v-for="(model) in suggestedModels"
            />
          </vs-select>

          <div class="loadingContainer absolute inset-0 pointer-events-none p-3 pr-6 flex items-center justify-end">
            <!-- <feather-icon icon="RotateCwIcon" class="animate-spin text-primary"/>
            <label class="text-primary">buscando</label> -->
          </div>
        </div>
        <b-button
          v-if="selectedModelId && appendClearOption && !isDisabled"
          class="flex-grow-0 flex-shrink-0 h-auto btn-icon"
          variant="outline-danger"
          @click="clearSelection"
          v-tooltip.auto="{
            content: 'Limpar seleção',
            delay: { show: 1000, hide: 100 },
          }">
          <feather-icon
                icon="XIcon"
                class="text-danger stroke-current"/>
        </b-button>
      </div>
  </div>
</template>

<script>
import {
  BFormRow,
  BRow,
  BCol,
  //
  BButton,
  BInputGroup,
  BInputGroupAppend,
} from 'bootstrap-vue'

import SuggestionService from '@/services/api/SuggestionService'

export default {
  components: {
    BFormRow,
    BRow,
    BCol,
    //
    BButton,
    BInputGroup,
    BInputGroupAppend,
  },
  props: {
    onlySuggestion: {
      type: Boolean,
      default: true
    },
    placeholderText: {
      type: String,
      default: ''
    },
    model: {
      type: String,
      default: null
    },
    column: {
      type: String,
      default: null
    },
    max: {
      type: Number,
      default: 5
    },
    orderBy:{
      type: String,
      default: 'id'
    },
    itemTextFormat: {
      type: Function,
      default: (model) => {
        return model.name || model.title || model.description || model.id
      }
    },
    selectedModel: {
      type: Object,
      default: null
    },
    label: {
      type: String,
      default: null
    },
    noDataText: {
      type: String,
      default: 'Nenhum valor encontrado, informe outro valor para pesquisar.'
    },
    appendClearOption: {
      type: Boolean,
      default: true
    },
    color: {
      type: String,
      default: 'primary'
    },
    promptText: {
      type: String,
      default: 'Informe um valor para pesquisar.'
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    /**
     * Classe aplicada ao painel .vs-select--options quando o dropdown abre.
     * Necessário porque o Vuesax move esse nó para document.body (insertBody), fora do escopo do componente pai.
     */
    dropdownOptionsClass: {
      type: String,
      default: ''
    }
  },
  data: () => ({
    selectedModelId: null,
    suggestionService: null,
    suggestedModels: [],
    timeout: null,
    focused: false,
    searchInputValue: '',
    lastSuggestionCount: null
  }),

  model: {
    prop: 'selectedModel',
    event: 'selected-model'
  },

  watch: {
    dropdownOptionsClass() {
      this.syncDropdownOptionsClass()
    },
    max(v) {
      this.changeServiceParam('max', v)
    },
    model(v) {
      this.changeServiceParam('model', v)
    },
    column(v) {
      this.changeServiceParam('property', v)
    },
    /**
     * Quando o pai zera ou troca o v-model (ex.: limpar formulário de assinatura), o vs-select
     * mantinha id/texto antigos — sincroniza estado interno com a prop selectedModel.
     */
    selectedModel: {
      handler(val) {
        if (val == null || val === '' || (typeof val === 'object' && val.id == null)) {
          this.selectedModelId = null
          this.suggestedModels = []
          this.lastSuggestionCount = null
          this.$nextTick(() => {
            try {
              const inp = this.$refs.select && this.$refs.select.$refs && this.$refs.select.$refs.inputselect
              if (inp) {
                inp.value = ''
              }
            } catch (e) {
              /* refs ainda não montados */
            }
          })
        } else {
          this.selectedModelId = val.id
          this.suggestedModels = [val]
        }
      },
      immediate: true
    }
  },

  mounted() {
    this.suggestionService = SuggestionService.buildOnModel(
      this.model,
      this.column,
      this.max,
      [],
      this.$vs
    )
    this.guaranteeModelDisplay()
    this.syncDropdownOptionsClass()
  },

  beforeDestroy() {
    if (this._dropdownOptionsClassUnwatch) {
      this._dropdownOptionsClassUnwatch()
      this._dropdownOptionsClassUnwatch = null
    }
  },

  beforeUpdate() {
    this.guaranteeModelDisplay()
  },

  computed: {
    showAppendButtons() {
      return (this.selectedModelId && this.appendClearOption)
    },
    select() {
      return this.$refs['select']
    },
    searchinput() {
      return this.$refs['select'].$refs['inputselect']
    },
    noData() {

      if (!this.isEmpty(this.searchInputValue) && this.lastSuggestionCount === 0) {
        return this.noDataText
      }

      return this.promptText
    },
    translatedPlaceholderText() {
      const model = `${this.model.toString().toLowerCase()}`
      return this.placeholderText || this.$t(`placeholders.select_suggestion.${model}`)
    }
  },

  methods: {
    syncDropdownOptionsClass() {
      if (this._dropdownOptionsClassUnwatch) {
        this._dropdownOptionsClassUnwatch()
        this._dropdownOptionsClassUnwatch = null
      }
      if (!this.dropdownOptionsClass) return
      this.$nextTick(() => {
        const sel = this.$refs.select
        if (!sel || typeof sel.active === 'undefined') return
        const apply = () => {
          const el = sel.$refs && sel.$refs.vsSelectOptions
          if (!el || !this.dropdownOptionsClass) return
          if (sel.active) {
            el.classList.add(this.dropdownOptionsClass)
          } else {
            el.classList.remove(this.dropdownOptionsClass)
          }
        }
        this._dropdownOptionsClassUnwatch = this.$watch(
          () => sel.active,
          () => {
            // insertBody roda no $nextTick do vs-select; atrasar para o nó já estar no body
            setTimeout(apply, 0)
          },
          { immediate: true }
        )
      })
    },
    changeServiceParam(param, v) {
      if (this.suggestionService) this.suggestionService[param] = v
    },
    selectModel(id) {
      this.selectedModel = this.suggestedModels.find(model => model.id === id)
      this.suggestedModels = [this.selectedModel]
      this.emitSelection()
    },
    fill(model) {
      this.selectedModel = model
      this.suggestedModels = [model]
      this.selectedModelId = model.id
      this.emitSelection()
    },
    clearSelection() {
      this.reset()
    },
    guaranteeModelDisplay() {
      if (!this.isEmpty(this.selectedModel) && this.isEmpty(this.selectedModelId)) {
        this.selectedModelId = this.selectedModel.id
        this.suggestedModels = [this.selectedModel]
      }
    },
    fetchSuggestion(search) {
      if (search && search.length) {
        this.showLoading()
        this.suggestionService.fetchSuggestion(search).then(response => {
          this.lastSuggestionCount = response.length
          this.closeLoading()
          this.suggestedModels = response
          clearTimeout(this.timeout)
          this.timeout = null
          /**
           * O vs-select aplica filterItems em cada keyup no DOM *atual*. Se a resposta da API
           * chega depois, os itens antigos podem ser filtrados para zero e `clear` fica true
           * antes dos novos vs-select-item montarem — e-mail deixa de aparecer. Reaplica o
           * filtro após o patch do DOM para alinhar visibilidade ao input e aos novos itens.
           */
          this.$nextTick(() => {
            const sel = this.$refs.select
            const inp = sel && sel.$refs && sel.$refs.inputselect
            if (!sel || !inp) return
            if (response.length > 0) {
              sel.clear = false
              if (typeof sel.filterItems === 'function') {
                sel.filterItems(inp.value || '')
              }
            } else {
              sel.clear = true
            }
          })
        }, error => {
          this.closeLoading()
        })
      }
    },
    searchSuggestions() {
      if (this.timeout) clearTimeout(this.timeout)

      this.searchInputValue = this.searchinput.value // Update readonly variable.

      const search = this.searchinput.value
      if (!this.isEmpty(search)) {
        this.timeout = setTimeout(() => {
          this.fetchSuggestion(search)
        }, 500)
      } else {
        this.select.clear = false
        this.reset()
      }
    },
    emitSelection() {
      this.$emit('selected-model', this.selectedModel)
    },
    reset() {
      this.suggestedModels = []
      this.selectedModel = null
      this.selectedModelId = null
      this.searchinput.value = ''
      this.lastSuggestionCount = null
      this.emitSelection()
    },
    onFocus(event) {
      this.searchInputValue = this.searchinput.value // Update readonly variable.
      this.focused = true
      this.fetchSuggestion('')
    },
    showLoading() {
      const inputSelect = document.querySelector('.loadingContainer')
      if (inputSelect) {
        this.$vs.loading({
          container: inputSelect,
          scale: 0.6
        })
      }
    },
    closeLoading() {
      const inputSelect = document.querySelector('.loadingContainer')
      if (inputSelect) {
        this.$vs.loading.close(inputSelect)
      }
    }
  },


}
</script>

<style lang="scss">
@import 'bootstrap/scss/functions';
@import '~@core/scss/base/bootstrap-extended/variables';
@import 'bootstrap/scss/variables';
@import '~@core/scss/base/components/variables-dark';

  .select-suggestion {
    height: min-content;
  }
</style>
